import { type ReactNode, createContext, useCallback, useContext, useState } from 'react'
import { Redirect, useLocation } from 'react-router-dom'
import TabContext from '@mui/lab/TabContext'

const registeredFormTabsCtx = createContext<{
  [key: string]: string[]
}>({})

interface RegisterFieldInFormTab {
  (tab: string, field: string): void
}

const registerFieldInFormTabCtx = createContext<RegisterFieldInFormTab>((_tab: string, _field: string) => {})

export function useRegisteredFormTabs() {
  return useContext(registeredFormTabsCtx)
}

export function useRegisterFormFieldInFormTab() {
  return useContext(registerFieldInFormTabCtx)
}

interface FormTabContextProps {
  children: ReactNode
  initialValue: string
}

export function FormTabContext({ children, initialValue }: FormTabContextProps) {
  const location = useLocation()
  const [tabs, setTabs] = useState<Record<string, string[]>>({})

  const register = useCallback(
    (tab: string, field: string) => {
      setTabs(prevTabs => {
        const matchingTab = prevTabs[tab]

        if (matchingTab?.includes(field)) {
          return prevTabs
        }

        return {
          ...prevTabs,
          [tab]: matchingTab ? [...matchingTab, field] : [field]
        }
      })
    },
    [setTabs]
  )

  if (location.hash === '') {
    return <Redirect to={{ ...location, hash: initialValue }} />
  }

  return (
    <TabContext value={location.hash}>
      <registeredFormTabsCtx.Provider value={tabs}>
        <registerFieldInFormTabCtx.Provider value={register}>{children}</registerFieldInFormTabCtx.Provider>
      </registeredFormTabsCtx.Provider>
    </TabContext>
  )
}
